import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { useI18n, LANGUAGES } from "../lib/i18n";
import { LogOut, Download, Globe, Check, MessageCircle, Phone, UserCircle, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import NotificationBell from "./NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();

  const dashPath = user
    ? user.role === "admin" ? "/admin"
    : user.role === "doctor" ? "/doctor"
    : "/patient"
    : "/login";

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      {/* Top contact strip — Max Healthcare style */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white text-[12px] sm:text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <div className="hidden sm:flex items-center gap-1.5 text-white/90">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
            <span className="font-medium">24×7 second opinions from NIMHANS &amp; AIIMS alumni</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("noi:open-wa-intake", { detail: { source: "topbar" } }))}
              className="inline-flex items-center gap-1.5 hover:text-white text-white/95 font-medium"
              data-testid="topbar-whatsapp"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">WhatsApp Us</span>
              <span className="hidden md:inline">(24×7)</span>
            </button>
            <a
              href="tel:+918317674160"
              className="inline-flex items-center gap-1.5 hover:text-white text-white/95 font-medium"
              data-testid="topbar-phone"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">+91 83176 74160</span>
              <span className="sm:hidden">Call</span>
            </a>
            <a
              href="tel:+919441724072"
              className="hidden md:inline-flex items-center gap-1.5 hover:text-white text-white/95 font-medium border-l border-white/20 pl-4"
              data-testid="topbar-assistance"
              title="General assistance line"
            >
              <span className="text-[10px] uppercase tracking-wider opacity-80">Assist</span>
              <span>+91 94417 24072</span>
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link">
          {/* Mobile: brain icon only · Desktop: full wordmark */}
          <img
            src="/icons/nav-brain.png"
            alt="NeuroOpinion India"
            className="w-9 h-9 sm:hidden object-contain"
            width={36}
            height={36}
          />
          <img
            src="/icons/wordmark.png"
            alt="NeuroOpinion India"
            className="hidden sm:block h-9 w-auto"
            height={36}
          />
        </Link>
        <div className="flex items-center gap-2">
          {/* Meet specialists — direct visibility to the doctor showcase.
              Hidden on mobile to keep the top nav clean. */}
          <Link
            to="/doctors"
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
            data-testid="nav-doctors-link"
          >
            🩺 Meet Specialists
          </Link>
          {/* Language picker — dropdown supports all major Indian official languages */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="lang-picker-trigger"
                aria-label="Choose language"
              >
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="font-heading">{currentLang.glyph}</span>
                <span className="text-xs text-slate-500">·</span>
                <span className="uppercase tracking-wide text-xs">{currentLang.short}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Language / भाषा
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onSelect={() => setLang(l.code)}
                  className="flex items-center gap-2 cursor-pointer"
                  data-testid={`lang-${l.code}`}
                >
                  <span className="font-heading text-base w-5 text-blue-700">{l.glyph}</span>
                  <span className="flex-1">
                    <span className="text-sm font-medium text-slate-900">{l.native}</span>
                    {l.native !== l.name && (
                      <span className="text-[11px] text-slate-500 ml-1.5">({l.name})</span>
                    )}
                  </span>
                  {lang === l.code && <Check className="w-4 h-4 text-blue-700" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <NotificationBell />
              <Button variant="ghost" onClick={() => nav(dashPath)} data-testid="nav-dashboard-btn">
                {t("dashboard")}
              </Button>
              <Button variant="outline" onClick={() => { logout(); nav("/"); }} data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4 mr-1" /> {t("logout")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/install")} className="hidden sm:inline-flex" data-testid="nav-install-btn">
                <Download className="w-4 h-4 mr-1" /> Get app
              </Button>

              {/* MOBILE — single dropdown with all 3 sign-in options (admin was previously hidden) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="md:hidden border-slate-300 text-slate-700 rounded-full px-3"
                    data-testid="nav-mobile-signin-btn"
                  >
                    <UserCircle className="w-4 h-4 mr-1.5" /> Sign in <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70"/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                    Choose account type
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => nav("/login?as=patient")} data-testid="nav-mobile-patient-link">
                    Patient sign in / Sign up
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => nav("/signup")} data-testid="nav-mobile-doctor-link">
                    Doctor sign in / Apply
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => nav("/hospital/signup")} data-testid="nav-mobile-hospital-link">
                    Hospital partner
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => nav("/login?as=admin")} data-testid="nav-mobile-admin-link">
                    Admin sign in
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* DESKTOP — three separate buttons (md+) */}
              <Button
                variant="outline"
                onClick={() => nav("/login?as=patient")}
                className="hidden md:inline-flex border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4"
                data-testid="nav-login-btn"
              >
                Patient signup / Login
              </Button>
              <Button
                onClick={() => nav("/signup")}
                className="hidden md:inline-flex bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4"
                data-testid="nav-signup-btn"
              >
                Doctor signup / Login
              </Button>
              <Button
                variant="outline"
                onClick={() => nav("/login?as=admin")}
                className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 hidden md:inline-flex"
                data-testid="nav-admin-btn"
              >
                Admin Login
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
